import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Tooltip, Typography } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Ticket, BatteryStatus, NodeRecovery, MeteredAsset } from '../../types/triage';

dayjs.extend(relativeTime);

const DASHBOARD_URL = (import.meta.env.VITE_DASHBOARD_URL || 'https://dashboard.sensorflow.cloud').replace(/\/$/, '');

interface NodeInfoBarProps {
  ticket: Ticket;
  nodeDetail: any | null;
  batteryStatus: BatteryStatus | null;
  nodeHistory?: any[];
  // Recovery state from sensorflow_analytics.m_node_recovery_lookup. When present, shows
  // a chip indicating whether the node has come back online and the MTTR
  // — useful for ops triage: a "recovered (2d)" chip says "this self-healed
  // in 2 days, low urgency"; "still offline (314d)" says "this is dead."
  recovery?: NodeRecovery | null;
  // Assets metered by this node (energy nodes only). When present, shown as a
  // "Metering" row — these nodes have no room key, so key_name is "unset" and
  // this is the meaningful identity: what equipment the node is measuring.
  meteredAssets?: MeteredAsset[] | null;
}

// Node age is anchored to the first 'mapped' lifecycle event — the same basis
// as the classifier's nodeAgeDays signal (node_to_slot_mapping.mapped_time in
// signalCollector.ts's getNodeAgeAndProperties). Deliberately excludes test jig
// entry_time (factory/QA test date, pre-deployment) so this field always agrees
// with the age the HW_EOL rules actually gate on. Returns null when unavailable.
function computeNodeBirth(nodeHistory: any[] | undefined): string | null {
  let earliest: string | null = null;
  for (const e of nodeHistory || []) {
    if (e?.event_type?.toLowerCase?.() === 'mapped' && e.started) {
      if (!earliest || new Date(e.started).getTime() < new Date(earliest).getTime()) {
        earliest = e.started;
      }
    }
  }
  return earliest;
}

// MTTR string used by the recovery chip. Coerces the time_to_recovery_days
// column (numeric → string in pg) to a number and renders with day granularity.
function formatRecoveryDays(raw: number | string | null | undefined): string {
  if (raw == null) return '?';
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return '?';
  if (n < 1) return '<1d';
  return `${Math.round(n)}d`;
}

const NodeInfoBar: React.FC<NodeInfoBarProps> = ({ ticket, nodeDetail, batteryStatus, nodeHistory, recovery, meteredAssets }) => {
  const metered = (meteredAssets || []).filter((a) => a.name);
  const nodeBirth = useMemo(
    () => computeNodeBirth(nodeHistory),
    [nodeHistory]
  );

  // Recovery chip — color codes the operational state for fast scanning:
  //   green: recovered, low MTTR (self-healed, deprioritize)
  //   gold:  recovered, slow MTTR (lingered, worth noting)
  //   red:   still offline, long silence (urgent)
  //   gray:  recovery model has no opinion (gap-affected or no data)
  const recoveryChip = useMemo(() => {
    if (!recovery) return null;
    if (recovery.is_gap_affected) {
      return { color: 'default' as const, label: 'recovery: gap', tooltip: 'sensorflow_analytics recovery lookup blocked by telemetry gap (Aug 2025–Jan 2026)' };
    }
    if (recovery.is_recovered) {
      const mttr = formatRecoveryDays(recovery.time_to_recovery_days);
      const n = typeof recovery.time_to_recovery_days === 'number'
        ? recovery.time_to_recovery_days
        : Number(recovery.time_to_recovery_days);
      const color = Number.isFinite(n) && n >= 14 ? 'gold' : 'green';
      return { color, label: `recovered · ${mttr}`, tooltip: `Came back online after ${mttr}` };
    }
    // Not recovered. Compute days since offline_since_day to surface urgency.
    if (!recovery.offline_since_day) {
      return { color: 'default' as const, label: 'recovery: unknown', tooltip: 'No recovery data for this node yet' };
    }
    const days = Math.max(0, Math.floor((Date.now() - new Date(recovery.offline_since_day).getTime()) / 86400000));
    const color = days >= 30 ? 'red' : days >= 7 ? 'orange' : 'gold';
    return { color, label: `silent · ${days}d`, tooltip: `Not yet recovered. Offline since ${recovery.offline_since_day}` };
  }, [recovery]);
  const dashboardLink = DASHBOARD_URL && ticket.key_id
    ? `${DASHBOARD_URL}/locations/${ticket.location_id}/keys/${ticket.key_id}`
    : null;

  return (
    <Card size="small" style={{ marginBottom: 12, overflow: 'hidden' }} styles={{ body: { padding: '8px 12px' } }}>
      <Descriptions
        size="small"
        column={{ xs: 2, sm: 2, md: 3, lg: 4, xl: 4 }}
        colon={false}
        labelStyle={{ color: '#8c8c8c', fontSize: 11, paddingBottom: 2 }}
        contentStyle={{ fontSize: 12, paddingBottom: 4 }}
      >
        <Descriptions.Item label="MAC">
          <Typography.Text copyable code style={{ fontSize: 11 }}>
            {ticket.observable_id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          {nodeDetail?.node_sub_type || nodeDetail?.node_type || '--'}
        </Descriptions.Item>
        {metered.length > 0 ? (
          // Energy nodes have no room key (key_name = "unset"); what matters is
          // the equipment they meter, one asset per CT channel.
          <Descriptions.Item label={`Metering (${metered.length})`}>
            <Tooltip
              title={metered.map((a) => (
                <div key={a.channel_index}>
                  ch{a.channel_index}: {a.name?.trim()}
                  {a.asset_type ? ` · ${a.asset_type}` : ''}
                  {a.hvac_system_type ? ` · ${a.hvac_system_type}` : ''}
                </div>
              ))}
            >
              <Typography.Text ellipsis style={{ maxWidth: 200 }}>
                {metered.map((a) => a.name?.trim()).join(', ')}
              </Typography.Text>
            </Tooltip>
          </Descriptions.Item>
        ) : (
          <Descriptions.Item label="Key">
            {dashboardLink ? (
              <Typography.Link href={dashboardLink} target="_blank" ellipsis style={{ maxWidth: 140, fontSize: 12 }}>
                {ticket.key_name || '--'} <LinkOutlined />
              </Typography.Link>
            ) : (
              <Typography.Text ellipsis style={{ maxWidth: 140 }}>
                {ticket.key_name || '--'}
              </Typography.Text>
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Room">
          <Typography.Text ellipsis style={{ maxWidth: 140 }}>
            {ticket.autoset_name || ticket.observable_name || '--'}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Battery">
          {batteryStatus ? (() => {
            const v = Number(batteryStatus.avg_battery_lvl);
            return (
              <span style={{ color: v < 2.2 ? '#ff4d4f' : v < 2.5 ? '#faad14' : '#00A651', fontWeight: 500 }}>
                {v.toFixed(2)}V
              </span>
            );
          })() : '--'}
        </Descriptions.Item>
        <Descriptions.Item label="Last Seen">
          {nodeDetail?.last_seen ? dayjs(nodeDetail.last_seen).fromNow() : '--'}
        </Descriptions.Item>
        <Descriptions.Item label="Node Age">
          {nodeBirth ? (
            <Tooltip title={`Since ${dayjs(nodeBirth).format('YYYY-MM-DD')}`}>
              <span style={{ fontWeight: 500 }}>{dayjs(nodeBirth).fromNow(true)}</span>
            </Tooltip>
          ) : (
            '--'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>{ticket.status_name || 'Unknown'}</Tag>
        </Descriptions.Item>
        {recoveryChip && (
          <Descriptions.Item label="Recovery">
            <Tooltip title={recoveryChip.tooltip}>
              <Tag color={recoveryChip.color} style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
                {recoveryChip.label}
              </Tag>
            </Tooltip>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default NodeInfoBar;
